import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Loading } from "../../components";
import { PageHeader, PageLayout, Select, StatePanel } from "../../components/ui";
import { UserContext, useEntitlements } from "../../contexts";
import { canUseOpenedFeature } from "../../contexts/openedFeature";
import { usePoll } from "../../hooks/usePoll";
import { fetchAuctions, fetchBelowTrade, fetchPrivateSellers, reportDateLabel, timeLabel } from "./dealsData";
import PrivateSellersTab from "./PrivateSellersTab";
import AuctionsTab from "./AuctionsTab";
import BelowTradeTab from "./BelowTradeTab";
import { Ch24Status, OutreachProvider, WatchlistButton } from "./ContactSeller";
import "./deals.scss";

// Deals — the overnight buy opportunities:
//   /deals/private-sellers   Chrono24 private sellers (the 10:00 email, browsable by date)
//   /deals/auctions          WatchCollecting live lots: ending within 6h, and all live
//   /deals/below-trade       market + trade prices 10%+ under the trade median
// Design: claude.ai artifact "Deals Page" (approved before build).
//
// Each tab is its own Feature Matrix row (Alex, 2026-10-27): master admins
// see every tab; a merchant sees a tab when canUseOpenedFeature allows its
// row (the API applies the same rule — `feature.open:<key>`). Contact seller
// and the Watchlist are their own row, "Automated private seller contact"
// (deals_contact_seller): a merchant sees only their store's enquiries. The
// Chrono24 pill manages the sender account's login and stays master-only.
const TABS = [
  { key: "private-sellers", label: "Private sellers", feature: "buyops_ch24_private_sellers" },
  { key: "auctions", label: "Auctions", feature: "deals_auctions" },
  { key: "below-trade", label: "Underpriced", feature: "buyops_underpriced_alerts" },
];

const useLoad = (loader, deps) => {
  const [state, setState] = useState({ status: "loading", data: null });
  const load = useCallback(() => {
    let live = true;
    setState((s) => ({ status: s.data ? "refreshing" : "loading", data: s.data }));
    loader()
      .then((data) => live && setState({ status: "ready", data }))
      .catch(() => live && setState((s) => ({ status: "error", data: s.data })));
    return () => { live = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  // The same load for a poll: resolves/rejects with the request so usePoll
  // can back off and stop. Same state transitions as `load`.
  const poll = useCallback(() => {
    setState((s) => ({ status: s.data ? "refreshing" : "loading", data: s.data }));
    return loader().then(
      (data) => setState({ status: "ready", data }),
      (error) => {
        setState((s) => ({ status: "error", data: s.data }));
        throw error;
      }
    );
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => load(), [load]);
  return [state, load, poll];
};

export default function DealsPage() {
  const { user } = useContext(UserContext);
  const entitlements = useEntitlements();
  const isMaster = !!user?.is_master_admin;
  // "Automated private seller contact" — Contact seller + the Watchlist.
  const canContact = canUseOpenedFeature(user, entitlements, "deals_contact_seller");
  const openTabs = useMemo(
    () => TABS.filter((t) => canUseOpenedFeature(user, entitlements, t.feature)),
    [user, entitlements]
  );
  const can = (key) => openTabs.some((t) => t.key === key);
  const allowed = openTabs.length > 0;
  const { tab: tabParam } = useParams();
  const { search, pathname } = useLocation();
  const history = useHistory();
  // An unknown or not-included tab opens the first one the user has.
  const tab = can(tabParam) ? tabParam : openTabs[0]?.key ?? "private-sellers";

  const params = new URLSearchParams(search);
  const date = params.get("date") || "";
  const setDate = (d) => {
    const next = new URLSearchParams(search);
    if (d) next.set("date", d); else next.delete("date");
    history.replace(`${pathname}${next.toString() ? `?${next}` : ""}`);
  };

  const [region, setRegion] = useState("uk");
  const [days, setDays] = useState(7);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [belowRegion, setBelowRegion] = useState(null);

  // Each tab loads only when the user has it — the others would 403.
  const canPs = can("private-sellers");
  const canBelow = can("below-trade");
  const canAuctions = can("auctions");
  const [ps, reloadPs] = useLoad(() => (canPs ? fetchPrivateSellers({ date }) : Promise.resolve(null)), [canPs, date]);
  const [below, reloadBelow] = useLoad(() => (canBelow ? fetchBelowTrade({ days, type, q, region: belowRegion }) : Promise.resolve(null)), [canBelow, days, type, q, belowRegion]);
  const [auctions, reloadAuctions, pollAuctions] = useLoad(() => (canAuctions ? fetchAuctions({ withinHours: 336 }) : Promise.resolve(null)), [canAuctions]);

  // Auctions are live: refresh the list every 2 minutes while open (the box posts every 2-5).
  // usePoll (M4.1): a failed refresh backs off, 401/403/404 stop it, a hidden tab asks nothing.
  usePoll(pollAuctions, { interval: 2 * 60 * 1000, enabled: canAuctions && tab === "auctions" });

  const tabs = useMemo(() => {
    const counts = {
      "private-sellers": ps.data?.summary?.total,
      // Every live lot (the tab shows "Ending soon" and "All live").
      auctions: auctions.data?.feed?.stale ? 0 : auctions.data?.items?.length,
      "below-trade": below.data?.summary?.total,
    };
    return openTabs.map((t) => ({ label: t.label, to: `/deals/${t.key}`, count: counts[t.key] ?? undefined }));
  }, [ps.data, auctions.data, below.data, openTabs]);

  if (!user) return <Loading />;
  if (!allowed) return <PageLayout><StatePanel state="permission" headingLevel={2} /></PageLayout>;

  const run = ps.data?.run;
  const runs = ps.data?.runs || [];
  const dateOptions = runs.map((r) => ({
    value: String(r.report_date).slice(0, 10),
    label: `${reportDateLabel(r.report_date)} · ${r.items_count}`,
  }));

  const reportMeta = tab === "private-sellers" && ps.data?.date ? (
    <div className="deals-meta">
      <span className="deals-meta__text">
        {/* The box re-uploads through the day (hourly, Alex 2026-11-02): say when, not "03:00". */}
        Report of <strong>{reportDateLabel(ps.data.date)}</strong>{run?.uploaded_at ? ` · updated ${timeLabel(run.uploaded_at)}` : ""}
      </span>
      {run?.incomplete ? <span className="deals-chip deals-chip--warm">Incomplete</span> : null}
      {/* No email status: the 10:00 email is switched off (Alex, 2026-10-19). */}
      {dateOptions.length > 1 ? (
        <Select
          className="deals-meta__date"
          aria-label="Report date"
          options={dateOptions}
          value={String(ps.data.date).slice(0, 10)}
          onChange={(v) => setDate(v)}
        />
      ) : null}
    </div>
  ) : null;

  const page = (
    <PageLayout className="deals-page">
      <PageHeader
        title="Deals"
        description="Buy opportunities found overnight."
        tabs={tabs}
        activePath={`/deals/${tab}`}
        // Watchlist and the Chrono24 login belong to Private sellers only: hidden on Auctions and
        // Underpriced (Alex, 2026-11-02).
        topbarActions={<div className="deals-topbar">{reportMeta}{tab === "private-sellers" ? <><WatchlistButton /><Ch24Status /></> : null}</div>}
      />
      <div className="deals-body">
        {tab === "private-sellers" ? (
          <PrivateSellersTab state={ps} onRetry={reloadPs} region={region} onRegion={setRegion} />
        ) : null}
        {tab === "auctions" ? <AuctionsTab state={auctions} onRetry={reloadAuctions} /> : null}
        {tab === "below-trade" ? (
          <BelowTradeTab state={below} onRetry={reloadBelow} days={days} onDays={setDays} type={type} onType={setType} q={q} onSearch={setQ} region={belowRegion} onRegion={setBelowRegion} />
        ) : null}
      </div>
    </PageLayout>
  );

  // Contact seller and the Watchlist render nothing outside the provider, so
  // without the row they simply are not there.
  return canContact ? <OutreachProvider enabled manage={isMaster}>{page}</OutreachProvider> : page;
}
